import { useEffect, useMemo, useState } from "react";
import { useAttorneyHubDispatch } from "app/realtime-store/redux-store";

// Redux actions for populating related entities
import { populateDocuments } from "documents/entities/document/store/documents-redux-slice";
import { populateIndividuals } from "marketplace/entities/individual/realtime-store/individuals-redux-slice";
import { populateForums } from "messaging/entities/forum/store/forums-redux-slice";

// Permissions response type (adjust path if needed)
import PaginatedResponse from "common/contracts/paginated-response";
import { PermissionsByGranteeId } from "permissions/entities/permission/api/permissions-api-service";
import { populateBarebonesProposals } from "work/entities/proposal/store/proposals-redux-slice";
import { populateBarebonesWorkAgreements } from "work/entities/work-agreement/store/work-agreement-redux-slice";
import { useSession } from "users/session/session-context";
import Guid from "common/values/guid/guid";
import { DocumentAccess } from "permissions/entities/permission/resources/documents-resources";
import { ForumAccess } from "permissions/entities/permission/resources/messaging-resources";
import { ProposalAccess, WorkAgreementAccess } from "permissions/entities/permission/resources/work-resources";
import { EntityAgreementAccess } from "permissions/entities/permission/resources/legal-entities-resources";
import _ from "lodash";
import { populateEntityAgreements } from "legal-entities/entities/entity-agreement/store/entity-agreements-redux-slice";

/**
 * Hook: usePopulateRelatedEntities
 *
 * Consumes a permissions response and dispatches populate actions
 * for all related entity types (individuals, documents, forums, proposals, work agreements, entity agreements).
 *
 * Returns an `isRefetching` flag that is true while dispatching occurs.
 */
export default function usePopulateRelatedEntities(
  permissionsResponse?: PaginatedResponse<PermissionsByGranteeId>
): {
  isRefetching: boolean,
  userIds: Guid[],
  documentIds: Guid[],
  forumIds: Guid[],
  proposalIds: Guid[],
  workAgreementIds: Guid[],
  entityAgreementIds: Guid[],
} {
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const [isRefetching, setIsRefetching] = useState(false);

  // Compute IDs for lookups
  const {
    userIds,
    documentIds,
    forumIds,
    proposalIds,
    workAgreementIds,
    entityAgreementIds,
  } = useMemo(() => {
    const isGuid = (id: Guid | undefined | null): id is Guid =>
      !!id && !id.isEqualTo(Guid.empty);

    const data = permissionsResponse?.data ?? [];
    const permissions = data.flatMap((g) => g.recentPermissionsPreview ?? []);

    const _uniqueGranteeIds = _.uniqBy(
      data.map((g) => g.granteeUserId).filter(isGuid),
      (id) => id.value
    );
    const _uniqueGrantorIds = _.uniqBy(
      permissions.map((p) => p.grantorUserId).filter(isGuid),
      (id) => id.value
    );
    const _documentIds = _.uniqBy(
      permissions
        .filter((p) => p.resource instanceof DocumentAccess)
        .map((p) => (p.resource as DocumentAccess).documentId)
        .filter(isGuid),
      (id) => id.value
    );
    const _forumIds = _.uniqBy(
      permissions
        .filter((p) => p.resource instanceof ForumAccess)
        .map((p) => (p.resource as ForumAccess).forumId)
        .filter(isGuid),
      (id) => id.value
    );
    const _proposalIds = _.uniqBy(
      permissions
        .filter((p) => p.resource instanceof ProposalAccess)
        .map((p) => (p.resource as ProposalAccess).proposalId)
        .filter(isGuid),
      (id) => id.value
    );
    const _workAgreementIds = _.uniqBy(
      permissions
        .filter((wa) => wa.resource instanceof WorkAgreementAccess)
        .map((wa) => (wa.resource as WorkAgreementAccess).workId)
        .filter(isGuid),
      (id) => id.value
    );
    const _entityAgreementIds = _.uniqBy(
      permissions
        .filter((ea) => ea.resource instanceof EntityAgreementAccess)
        .map((ea) => (ea.resource as EntityAgreementAccess).entityAgreementId)
        .filter(isGuid),
      (id) => id.value
    );

    return {
      uniqueGranteeIds: _uniqueGranteeIds,
      uniqueGrantorIds: _uniqueGrantorIds,
      userIds: [..._uniqueGranteeIds, ..._uniqueGrantorIds].filter(isGuid),
      documentIds: _documentIds,
      forumIds: _forumIds,
      proposalIds: _proposalIds,
      workAgreementIds: _workAgreementIds,
      entityAgreementIds: _entityAgreementIds,
    };
  }, [permissionsResponse]);

  useEffect(() => {
    if (!permissionsResponse) return;

    setIsRefetching(true);

    const promises = [
      dispatch(populateIndividuals({ session, userIds })),
      dispatch(populateDocuments({ session, documentIds })),
      dispatch(populateForums({ session, forumIds })),
      dispatch(populateBarebonesProposals({ session, proposalIds })),
      dispatch(populateBarebonesWorkAgreements({ session, workAgreementIds })),
      dispatch(populateEntityAgreements({ session, entityAgreementIds })),
    ];

    let active = true;

    (async () => {
      try {
        await Promise.allSettled(promises as Array<Promise<unknown>>);
      } finally {
        if (active) setIsRefetching(false);
      }
    })();

    return () => {
      active = false;
      for (const p of promises) p?.abort?.();
    };
  }, [
    permissionsResponse,
    dispatch,
    session,
    userIds,
    documentIds,
    forumIds,
    proposalIds,
    workAgreementIds,
    entityAgreementIds,
  ]);

  return {
    isRefetching,
    userIds,
    documentIds,
    forumIds,
    proposalIds,
    workAgreementIds,
    entityAgreementIds,
  };
}
